// NavDropdown.js — P11: shared grouped-nav dropdown (Study Abroad / Services
// / Resources). A "disclosure" pattern, not a full ARIA menu widget — items
// are plain links a screen-reader user can Tab through normally, with arrow
// keys layered on top as a convenience, not a replacement. Never traps
// focus: Tab exits forward past the last item exactly like any other link.
import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

let idCounter = 0;

export default function NavDropdown({ label, items, onNavigate, badgeCount = 0, isOpen, onOpen, onClose: onCloseRequest }) {
  const [menuId] = useState(() => `nav-dropdown-${++idCounter}`);
  const triggerRef = useRef(null);
  const itemRefs = useRef([]);
  const closeTimerRef = useRef(null);
  const open = isOpen;

  const close = (returnFocus) => {
    onCloseRequest();
    if (returnFocus) triggerRef.current?.focus();
  };

  const handleTriggerClick = (e) => {
    e.preventDefault();
    // A pending hover-driven close (see handleMouseLeave) must not be left
    // to fire 200ms later and silently undo this click's result — e.g. a
    // touch/click that arrives just after the pointer briefly grazed off
    // the trigger.
    clearTimeout(closeTimerRef.current);
    if (open) {
      onCloseRequest();
    } else {
      onOpen();
    }
  };

  const focusItem = (index) => {
    const el = itemRefs.current[index];
    if (el) el.focus();
  };

  const handleTriggerKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      clearTimeout(closeTimerRef.current);
      onOpen();
      requestAnimationFrame(() => focusItem(0));
    } else if (e.key === 'Escape' && open) {
      close(true);
    }
  };

  const handleItemKeyDown = (e, index) => {
    const count = items.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusItem((index + 1) % count);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusItem((index - 1 + count) % count);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusItem(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusItem(count - 1);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close(true);
    }
    // Tab is intentionally left alone — it moves to the next/previous
    // focusable element in document order, same as it would for any link.
  };

  // Desktop hover — mirrors the previous "Other" dropdown's behaviour.
  // Keyboard users never rely on these (Enter/Space/ArrowDown do instead).
  const handleMouseEnter = () => {
    clearTimeout(closeTimerRef.current);
    onOpen();
  };
  const handleMouseLeave = () => {
    // Guarded by the parent (Navbar) rather than a local flag: onClose only
    // clears *this* dropdown's slot, so a mouse move straight onto the next
    // trigger (which calls its own onOpen synchronously) isn't undone when
    // this timer fires 200ms later — the two dropdowns no longer render
    // open at the same time even for that window.
    closeTimerRef.current = setTimeout(() => onCloseRequest(), 200);
  };

  const handleItemClick = () => {
    onCloseRequest();
    onNavigate?.();
  };

  return (
    <li
      className="nav-item dropdown-item"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        className="nav-links dropdown-toggle"
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        ref={triggerRef}
      >
        {label} <span aria-hidden="true">▾</span>
        {badgeCount > 0 && (
          <span className="nav-badge" aria-label={`${badgeCount} live announcements`}>
            {badgeCount}
          </span>
        )}
      </button>
      <ul id={menuId} className={`dropdown-menu${open ? ' show' : ''}`}>
        {items.map((item, index) => (
          <li key={item.to}>
            <Link
              to={item.to}
              className="dropdown-link"
              ref={(el) => { itemRefs.current[index] = el; }}
              onKeyDown={(e) => handleItemKeyDown(e, index)}
              onClick={handleItemClick}
              tabIndex={open ? 0 : -1}
            >
              <span className="dropdown-link-label">
                {item.label}
                {item.label === 'Announcements' && badgeCount > 0 && (
                  <span className="nav-badge nav-badge--inline" aria-hidden="true">{badgeCount}</span>
                )}
              </span>
              {item.description && (
                <span className="dropdown-link-description">{item.description}</span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </li>
  );
}
